import { Grid, Typography } from "@material-ui/core";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useAppSelector } from "src/app-redux/hooks";
import { getAllMultiContainerGateDocuments } from "src/app-redux/vehicle/actions/vehicleActions";
import { selectNodVehicleDetails } from "src/app-redux/vehicle/vehicleSlice";
import { colorCodeListForGateIn, customColorCodeList } from "src/common/utils/app.dropdowns";
import { useStyles } from "./../style";
import IconButton from "@mui/material/IconButton";
import img from "src/assets/images/file_formats_logos/img.svg";
import doc from "src/assets/images/file_formats_logos/doc.svg";
import pdf from "src/assets/images/file_formats_logos/pdf.svg";
import xlsx from "src/assets/images/file_formats_logos/xlsx.svg";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import { downloadFile } from "src/app-redux/common-actions";

export interface IDrawerDetailsProps {}

interface IDetailFieldConfig {
  label: string;
  getValue: (detail: any) => any;
  format?: (value: any) => React.ReactNode;
}

interface IDetailRowConfig {
  left: IDetailFieldConfig;
  right?: IDetailFieldConfig;
}

const getDisplayValue = (value: any, format?: (value: any) => React.ReactNode) =>
  format ? format(value) : value ? value : "-";

const isEmptyDetailValue = (value: any, format?: (value: any) => React.ReactNode) => {
  const displayValue = getDisplayValue(value, format);
  return (
    displayValue === "-" ||
    displayValue === null ||
    displayValue === undefined ||
    displayValue === ""
  );
};

const DetailFieldContent = ({
  label,
  value,
  format,
}: {
  label: string;
  value: any;
  format?: (value: any) => React.ReactNode;
}) => (
  <>
    <Typography className="subHeading">{label}</Typography>
    <Typography className="subHeadingDetails">{getDisplayValue(value, format)}</Typography>
  </>
);

const formatRemarks = (remarksText: string) => {
  const remarksArray = remarksText.split("@``@");
  return remarksArray?.map((value: any, index: number) => (
    <>
      <div key={index}>{value ? value : ""}</div>
      {index !== remarksArray.length - 1 && remarksArray[0].length > 1 && <br />}
    </>
  ));
};

const getVisibleFields = (rows: IDetailRowConfig[], detail: any) =>
  rows.flatMap((row) => {
    const fields: { config: IDetailFieldConfig; value: any }[] = [];
    const leftValue = row.left.getValue(detail);
    if (!isEmptyDetailValue(leftValue, row.left.format)) {
      fields.push({ config: row.left, value: leftValue });
    }
    if (row.right) {
      const rightValue = row.right.getValue(detail);
      if (!isEmptyDetailValue(rightValue, row.right.format)) {
        fields.push({ config: row.right, value: rightValue });
      }
    }
    return fields;
  });

const DetailFieldRow = ({
  leftField,
  rightField,
}: {
  leftField: { config: IDetailFieldConfig; value: any };
  rightField?: { config: IDetailFieldConfig; value: any };
}) => (
  <Grid item xs={12} className="itemRow">
    <Grid container direction="row">
      <Grid item xs={1}></Grid>
      <Grid item xs={rightField ? 6 : 11}>
        <DetailFieldContent
          label={leftField.config.label}
          value={leftField.value}
          format={leftField.config.format}
        />
      </Grid>
      {rightField && (
        <Grid item xs={5}>
          <DetailFieldContent
            label={rightField.config.label}
            value={rightField.value}
            format={rightField.config.format}
          />
        </Grid>
      )}
    </Grid>
  </Grid>
);

const DetailSectionBlock = ({
  title,
  rows,
  detail,
}: {
  title: string;
  rows: IDetailRowConfig[];
  detail: any;
}) => {
  if (!detail) return null;

  const visibleFields = getVisibleFields(rows, detail);
  if (visibleFields.length === 0) return null;

  const packedRows: { config: IDetailFieldConfig; value: any }[][] = [];
  for (let i = 0; i < visibleFields.length; i += 2) {
    packedRows.push(visibleFields.slice(i, i + 2));
  }

  return (
    <>
      <Typography className="heading">{title}</Typography>
      <Grid container spacing={2} className="topContainer">
        {packedRows.map((pair, index) => (
          <DetailFieldRow key={index} leftField={pair[0]} rightField={pair[1]} />
        ))}
      </Grid>
    </>
  );
};

const vehicleDetailRows: IDetailRowConfig[] = [
  {
    left: { label: "Vehicle Number", getValue: (d) => d?.vehicleDetail?.vehicleRegNumber },
    right: { label: "Vehicle Status", getValue: (d) => d?.vehicleDetail?.vehicleStatus },
  },
  {
    left: { label: "Vehicle Type", getValue: (d) => d?.vehicleDetail?.vehicleType },
    right: { label: "Location at Terminal", getValue: (d) => d?.vehicleDetail?.terminalLocation },
  },
  {
    left: { label: "Gate Entry Opperator", getValue: (d) => d?.vehicleDetail?.entryBy },
    right: { label: "Slip Number", getValue: (d) => d?.vehicleDetail?.slipNumber },
  },
  {
    left: {
      label: "Commercial",
      getValue: (d) => d?.vehicleDetail?.commercial,
      format: (v) => (v ? "Yes" : "No"),
    },
    right: { label: "Chassis Number", getValue: (d) => d?.vehicleDetail?.vehicleChassisNumber },
  },
  {
    left: { label: "Vehicle Color", getValue: (d) => d?.vehicleDetail?.vehicleColor },
    right: { label: "Vehicle Brand", getValue: (d) => d?.vehicleDetail?.vehicleBrand },
  },
  {
    left: { label: "Vehicle Model", getValue: (d) => d?.vehicleDetail?.vehicleModel },
    right: { label: "Vehicle Make/Year", getValue: (d) => d?.vehicleDetail?.vehicleMake },
  },
  {
    left: {
      label: "Vehicle Channel",
      getValue: (d) => d?.vehicleDetail?.vehicleColorCode,
      format: (code) =>
        code ? colorCodeListForGateIn.find((val: any) => val.value == code)?.name ?? "-" : "-",
    },
    right: { label: "Agent Name", getValue: (d) => d?.vehicleDetail?.agentName },
  },
  {
    left: { label: "Agent’s Mobile Number", getValue: (d) => d?.vehicleDetail?.agentMobileNumber },
    right: {
      label: "WeBOC RMS Color Code",
      getValue: (d) => d?.vehicleDetail?.vehicleCustomColorCode,
      format: (code) =>
        code ? customColorCodeList.find((val: any) => val.value === code)?.name ?? "-" : "-",
    },
  },
  {
    left: { label: "Yard Name", getValue: (d) => d?.allotedYardDetails?.yardName },
    right: { label: "Yard Row Number", getValue: (d) => d?.allotedYardDetails?.rowName },
  },
  {
    left: { label: "Yard Row Slot", getValue: (d) => d?.allotedYardDetails?.slotNumber },
    right: { label: "CRM Number", getValue: (d) => d?.vehicleDetail?.crmNumber },
  },
  {
    left: { label: "E-Form Number", getValue: (d) => d?.vehicleDetail?.eFormNumber },
    right: { label: "Pass Number", getValue: (d) => d?.vehicleDetail?.passNumber },
  },
];

const driverDetailRows: IDetailRowConfig[] = [
  {
    left: { label: "Driver Name", getValue: (d) => d?.driverName },
    right: { label: "Driver’s Father Name", getValue: (d) => d?.driverFatherName },
  },
  {
    left: { label: "Driver’s Mobile Number", getValue: (d) => d?.driverMobileNumber },
    right: { label: "Driver’s CNIC", getValue: (d) => d?.driverCNIC },
  },
  {
    left: { label: "Travelling Doc Type", getValue: (d) => d?.travellingDocType },
    right: { label: "Travelling Doc Number", getValue: (d) => d?.travellingDocNumber },
  },
  {
    left: { label: "NTN", getValue: (d) => d?.driverNTN },
    right: { label: "Resident", getValue: (d) => d?.driverResident },
  },
];

const cargoDetailRows: IDetailRowConfig[] = [
  {
    left: { label: "Type Of Consignment", getValue: (d) => d?.consignmentTypeName },
    right: { label: "Type Of Cargo", getValue: (d) => d?.cargoTypeName },
  },
  {
    left: { label: "Container Number", getValue: (d) => d?.containerNumber },
    right: { label: "Container Seal Number", getValue: (d) => d?.sealNo },
  },
  {
    left: { label: "Good’s Declaration Number (GD#)", getValue: (d) => d?.gdNo },
    right: {
      label: "GD Date",
      getValue: (d) => d?.gdDate,
      format: (v) => (v ? moment(new Date(v)).format("DD-MM-YYYY") : "-"),
    },
  },
  {
    left: { label: "Supplier Name", getValue: (d) => d?.supplierName },
    right: { label: "WeBOC ID (IGM#)", getValue: (d) => d?.webBocId },
  },
];

const othersDetailRows: IDetailRowConfig[] = [
  {
    left: {
      label: "Entry Date and Time",
      getValue: (d) => d?.dateTime,
      format: (v) => (v ? moment(new Date(v)).format("DD-MM-YYYY hh:mm A") : "-"),
    },
    right: {
      label: "Gate-in Remarks",
      getValue: (d) => d?.notes,
      format: (notes) => (notes ? formatRemarks(notes) : "-"),
    },
  },
  {
    left: { label: "Customs Inspector Remarks", getValue: (d) => d?.remarks },
    right: { label: "Gate-out Remarks", getValue: (d) => d?.gateOutComments },
  },
  {
    left: { label: "WeBOC Vehicle Number", getValue: (d) => d?.webocVehicleNumber },
  },
];

export default function DrawerDetails(props: IDrawerDetailsProps) {
  const classes = useStyles();
  const vehicleDetailsResponse: any = useAppSelector(selectNodVehicleDetails);
  const dispatch = useDispatch();
  const [allDocs, setAllDocs] = useState<any[]>([]);
  const vehicleDetail = vehicleDetailsResponse?.vehicleDetail;

  useEffect(() => {
    vehicleDetailsResponse?.vehicleDetail?.vehicleId && getPictureGalleryContent();
  }, [vehicleDetailsResponse]);

  const getPictureGalleryContent = async () => {
    const res: any = await dispatch(
      getAllMultiContainerGateDocuments({
        gatePassId: vehicleDetailsResponse?.vehicleDetail?.vehicleId,
      })
    );
    if (res.payload) setAllDocs(res.payload);
  };

  const downloadDocument = async (item: any) => {
    const type =
      item.fileCategory === "Scan"
        ? 2
        : item.fileCategory === "Weight"
        ? 3
        : item.fileCategory === "Commercial"
        ? 4
        : 1;
    const fileMetaData = {
      url: `${process.env.REACT_APP_API_URL}/get-gate-doc-by-id/${item.id}?docType=${type}`,
      contentType: ".jpg",
      fileName: item.title,
    };
    dispatch(downloadFile(fileMetaData));
  };

  return (
    <div className={classes.drawerDetails}>
      <DetailSectionBlock
        title="Vehicle Details"
        rows={vehicleDetailRows}
        detail={vehicleDetailsResponse}
      />

      <DetailSectionBlock title="Driver Details" rows={driverDetailRows} detail={vehicleDetail} />

      <DetailSectionBlock title="Cargo Information" rows={cargoDetailRows} detail={vehicleDetail} />

      <DetailSectionBlock title="Others" rows={othersDetailRows} detail={vehicleDetail} />

      {allDocs.length > 0 && (
        <>
          <Typography className="heading">All Documents </Typography>
          <Grid container spacing={2} className="topContainer">
            <Grid container direction="row" className="itemRow" spacing={2}>
              <Grid
                item
                md={12}
                id="main-div"
                className={classes.drawerDetails}
                style={{
                  height: "max-content",
                }}
              >
                <Grid container direction="row">
                  {allDocs.map((item: any) => (
                    <Grid md={6} key={item.id} style={{ padding: "5px" }}>
                      <Grid container direction="row" className="itemRowTrail">
                        <Grid md={3} style={{ padding: "5px" }}>
                          <img
                            src={
                              item.extension === "doc" || item.extension === "docx"
                                ? doc
                                : item.extension === "jpg" ||
                                  item.extension === "jpeg" ||
                                  item.extension === "png"
                                ? img
                                : item.extension === "pdf"
                                ? pdf
                                : item.extension === "xlsx"
                                ? xlsx
                                : img
                            }
                            alt={item.extension}
                            width="25px"
                          />
                        </Grid>
                        <Grid md={6}>
                          <Typography className="subHeading itemName">
                            {item.originalFileName}
                          </Typography>
                        </Grid>
                        <Grid md={2}>
                          <IconButton
                            onClick={() => {
                              downloadDocument(item);
                            }}
                          >
                            <CloudDownloadIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
}
